import { bootstrapSessionOutcome } from '@jq4race/api-client';
import { RouterProvider } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { initAuthClient } from './app/auth-client';
import { setAuthBootOutcome } from './app/auth-state';
import { AppProviders } from './app/providers';
import { startQueryPersistenceForBoot } from './app/query-client';
import { router } from './app/router';
import { initSentry } from './app/sentry';
import { registerServiceWorker } from './app/service-worker';
import { cleanupSyncedBlobs } from './modules/offline/application/blob-cleanup';
import { cleanupOrphanedBlobs } from './modules/offline/application/cleanup-orphaned-blobs';
import { purgeOldOutboxRows } from './modules/offline/application/purge-old-outbox';
import { offlineDb } from './modules/offline/infrastructure/dexie/offline-db';
import './index.css';

initAuthClient();
initSentry();
registerServiceWorker();

// pwa-offline-first Slice 6 (task 6.10) originally ran its OWN boot-time
// sweep here for an item left `syncing` forever (app killed/crashed
// mid-upload). Third adversarial review finding M2 — DROPPED in favor of
// the foreground drainer's own in-tick reset (`STUCK_SYNCING_RESET_
// THRESHOLD_MS`, foreground-drainer.ts), which runs under the SAME Web
// Lock the drainer itself uses: this boot-time sweep ran with NO lock at
// all, so two tabs booting around the same time could interleave with the
// drainer's own lock-protected reset inconsistently. The drainer's boot
// trigger (`useForegroundDrainerTriggers`, mounted once identity resolves)
// already fires a tick immediately, which now resets any stuck row itself.

// pwa-offline-first Slice 6 (coordinator finding #5) — a captured photo
// whose form was abandoned before ever enqueueing has no outbox row and no
// other cleanup path. Fire-and-forget, same rationale as the sweep above.
void cleanupOrphanedBlobs(offlineDb).catch(() => {
  // Best-effort.
});

// second adversarial review LOW #9 — the foreground drainer sweeps synced
// blobs after each successful sync, but a blob whose item synced in a PRIOR
// session (e.g. the last tab was closed right after sync-success, before
// the drainer's own cleanup call landed) has no other cleanup path.
// Fire-and-forget, same rationale as the sweeps above.
void cleanupSyncedBlobs(offlineDb).catch(() => {
  // Best-effort.
});

// pwa-offline-first Slice 7 MEDIUM fix (adversarial review) — bounded
// outbox retention. Nothing before this fix ever deleted an outbox ROW
// (only its blobs, and only for a successfully-synced item — a discarded
// item's blobs were never swept by anything). Runs at boot in addition to
// the after-successful-sync sweep (foreground-drainer.ts's `purgeOldOutbox`
// dep) so a device that stays offline/idle for a long time, or was last
// closed right after a discard, still gets swept the next time it opens.
// Fire-and-forget, same rationale as the sweeps above.
void purgeOldOutboxRows(offlineDb).catch(() => {
  // Best-effort.
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('missing #root');

// Hydrate the auth store from the httpOnly refresh cookie before mounting
// the router. The protected-route guard is async (beforeLoad) and would
// otherwise read session=null and bounce the user to /login on every
// reload. If there's no valid cookie this resolves to an 'unauthenticated'
// or 'unreachable' outcome and the guard reacts accordingly.
//
// Adversarial-review fixes (Slice 3, finding #2 + its own follow-up
// review): uses bootstrapSessionOutcome() instead of the old
// bootstrapSession() so the offline-shell gate (_authenticated.tsx, via
// resolveAuthGate()) can tell "the request could not reach a healthy
// server" (network failure, an 8s boot timeout, or a 5xx/429 — from an
// operator's perspective a backend outage is exactly as unusable as no
// signal) apart from the server actually answering "no" — `navigator.
// onLine` alone is devtools-spoofable and never proved anything about the
// request that actually mattered. setAuthBootOutcome() records the outcome
// BEFORE the router mounts, so beforeLoad always reads a value from THIS
// page load's real boot attempt, not a stale/default one.
//
// The query-cache persister (task 3.5) is started right after, still before
// mount: it needs to know which org's buster to use, and the recovered
// session (or, offline, the cached offline profile) is exactly that answer
// — see startQueryPersistenceForBoot().
bootstrapSessionOutcome()
  .then((outcome) => {
    setAuthBootOutcome(outcome);
    return startQueryPersistenceForBoot(outcome.kind === 'session' ? outcome.session : null);
  })
  .finally(() => {
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <AppProviders>
          <RouterProvider router={router} />
        </AppProviders>
      </React.StrictMode>,
    );
  });
